import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, AlertTriangle, CheckCircle, XCircle, Trash2, Eye } from 'lucide-react';
import { renderApi } from '../../services/api';
import toast from 'react-hot-toast';

interface ScheduledPublication {
  id: string;
  storeName: string;
  scheduledAt: Date;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: Date;
  completedAt?: Date;
  error?: string;
  userEmail?: string;
  url?: string;
}

interface SchedulingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SchedulingModal: React.FC<SchedulingModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const [scheduledPublications, setScheduledPublications] = useState<ScheduledPublication[]>([]);
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [canceling, setCanceling] = useState<string | null>(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const loadScheduledPublications = async () => {
    try {
      setLoading(true);
      const response = await renderApi.listScheduledPublications();
      
      if (response.success) {
        setScheduledPublications(response.scheduledPublications);
      } else {
        toast.error('Erro ao carregar publicações agendadas');
      }
    } catch (error) {
      console.error('Erro ao carregar publicações agendadas:', error);
      toast.error('Erro ao carregar publicações agendadas');
    } finally {
      setLoading(false);
    }
  };

  // Validação melhorada no frontend
const schedulePublication = async () => {
  if (!scheduledDate || !scheduledTime) {
    toast.error('Selecione data e hora para agendar');
    return;
  }

  const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
  const now = new Date();
  
  // Validação mais robusta
  if (scheduledAt <= now) {
    const timeDiff = now.getTime() - scheduledAt.getTime();
    const minutesDiff = Math.floor(timeDiff / (1000 * 60));
    
    if (minutesDiff < 1) {
      toast.error('A data de agendamento deve ser pelo menos 1 minuto no futuro');
    } else if (minutesDiff < 60) {
      toast.error(`A data de agendamento deve ser futura (${minutesDiff} minutos atrás)`);
    } else {
      const hoursDiff = Math.floor(minutesDiff / 60);
      toast.error(`A data de agendamento deve ser futura (${hoursDiff} horas atrás)`);
    }
    return;
  }

  // Validar se não é muito no futuro (máximo 30 dias)
  const maxDateLimit = new Date();
  maxDateLimit.setDate(maxDateLimit.getDate() + 30);
  
  if (scheduledAt > maxDateLimit) {
    toast.error('A data de agendamento não pode ser mais de 30 dias no futuro');
    return;
  }

  try {
    setScheduling(true);
    
    const response = await renderApi.schedulePublication(scheduledAt.toISOString());
    
    if (response.success) {
      toast.success('Publicação agendada com sucesso!');
      setScheduledDate('');
      setScheduledTime('');
      await loadScheduledPublications();
    } else {
      toast.error('Erro ao agendar publicação');
    }
  } catch (error) {
    console.error('Erro ao agendar publicação:', error);
    toast.error('Erro ao agendar publicação');
  } finally {
    setScheduling(false);
  }
};

  const cancelScheduledPublication = async (publicationId: string) => {
    try {
      setCanceling(publicationId);
      
      const response = await renderApi.cancelScheduledPublication(publicationId);
      
      if (response.success) {
        toast.success('Publicação agendada cancelada com sucesso!');
        await loadScheduledPublications();
      } else {
        toast.error('Erro ao cancelar publicação agendada');
      }
    } catch (error) {
      console.error('Erro ao cancelar publicação agendada:', error);
      toast.error('Erro ao cancelar publicação agendada');
    } finally {
      setCanceling(null);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Aguardando';
      case 'processing':
        return 'Processando';
      case 'completed':
        return 'Concluído';
      case 'failed':
        return 'Falhou';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-700 bg-yellow-100';
      case 'processing':
        return 'text-blue-700 bg-blue-100';
      case 'completed':
        return 'text-green-700 bg-green-100';
      case 'failed':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadScheduledPublications();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop flex items-center justify-center z-[9999]">
      <div className="glass-modal modal-enter rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-3">
            <Calendar className="w-6 h-6 text-primary-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Agendar Publicação
              </h3>
              <p className="text-sm text-gray-600">
                Agende quando sua publicação será enviada para a Wake
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Form de Agendamento */}
          <div className="mb-6 p-4 border rounded-md bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Novo Agendamento</h4>
            
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data
                </label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  max={new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hora
                </label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={schedulePublication}
                  disabled={scheduling || !scheduledDate || !scheduledTime}
                  className="px-4 py-2 bg-gradient-primary text-white rounded-md hover:opacity-90 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {scheduling ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Agendando...</span>
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      <span>Agendar</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Lista de Agendamentos */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Agendamentos</h4>
            
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            ) : scheduledPublications.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Nenhum agendamento encontrado</p>
                <p className="text-xs text-gray-400 mt-1">
                  Agende uma publicação para vê-la aqui
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {scheduledPublications.map((publication) => (
                  <div
                    key={publication.id}
                    className="border rounded-md p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getStatusIcon(publication.status)}
                          <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(publication.status)}`}>
                            {getStatusText(publication.status)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatDate(publication.scheduledAt)}
                          </div>
                        </div>
                        
                        {publication.error && (
                          <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                            Erro: {publication.error}
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500">
                          Criado em: {formatDate(publication.createdAt)}
                          {publication.completedAt && (
                            <span className="ml-4">
                              Concluído em: {formatDate(publication.completedAt)}
                            </span>
                          )}
                        </div>
                        {publication.userEmail && publication.userEmail !== 'unknown@legacy.com' && (
                          <div className="mt-1 text-xs text-gray-400">
                            Agendado por: {publication.userEmail}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {publication.url && (
                          <button
                            onClick={() => window.open(publication.url, '_blank')}
                            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                            title="Visualizar JSON"
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                        )}
                        
                        {publication.status === 'pending' && (
                          <button
                            onClick={() => cancelScheduledPublication(publication.id)}
                            disabled={canceling === publication.id}
                            className="p-2 hover:bg-red-100 rounded-md transition-colors disabled:opacity-50"
                            title="Cancelar agendamento"
                          >
                            {canceling === publication.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                            ) : (
                              <Trash2 className="w-4 h-4 text-red-500" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            Total: {scheduledPublications.length} agendamento{scheduledPublications.length !== 1 ? 's' : ''}
          </div>
          <div className="text-xs text-gray-400">
            As publicações são executadas automaticamente no horário agendado
          </div>
        </div>
      </div>
    </div>
  );
};
